/**
 * Transaction Detail Modal
 * ========================
 * Full transaction details with reclassification functionality.
 *
 * Features:
 * - View complete transaction information
 * - Reclassify to any of the 48 BI categories
 * - Mark as reviewed or flagged
 * - View source document in PDF viewer
 * - Complete audit trail of reclassifications
 */

import React, { useState, useEffect, useRef } from 'react';
import { X, FileText, AlertCircle, CheckCircle, History } from 'lucide-react';
import api from '../../utils/api';
import PDFViewerModal from './PDFViewerModal';
import { useFocusTrap } from '../../hooks/useFocusTrap';

interface Transaction {
  id: string;
  date: string;
  vendor: string;
  description: string;
  amount: number;
  source_file: string;
  source_page: number;
  category: string;
  subcategory?: string;
  original_category?: string;
  is_reclassified: boolean;
  reclassification_reason?: string;
  checked: boolean;
  flagged: boolean;
  flag_reason?: string;
  needs_review: boolean;
}

interface TransactionDetailModalProps {
  transaction: Transaction;
  reportId: string;
  onClose: () => void;
  onUpdate: () => void;
}

interface ReclassificationHistory {
  timestamp: string;
  user: string;
  from_category: string;
  to_category: string;
  reason: string;
}

// All 48 BI Categories
const BI_CATEGORIES = [
  "Food & Beverage Inventory",
  "Salaries & Wages",
  "Rent",
  "Utilities",
  "Business Insurance",
  "Advertising",
  "Marketing",
  "Depreciation Expense",
  "Other Expenses",
  "Cost of Goods Sold",
  "Personnel Expenses",
  "Occupancy Expenses",
  "Insurance Expenses",
  "Professional Services",
  "Technology & Software",
  "Office & Supplies",
  "Vehicle & Transportation",
  "Bank & Financial Fees",
  "Taxes & Licenses",
  "Debt Service",
  "Transfer",
  "Sales Revenue",
  "Travel & Entertainment",
  "Inventory & Merchandise",
  "Miscellaneous",
  "Commissions",
  "Contract Labor",
  "Freight & Shipping",
  "Repairs & Maintenance",
  "Cleaning & Janitorial",
  "Security Services",
  "Training & Education",
  "Dues & Subscriptions",
  "Equipment Rental",
  "Storage Expense",
  "Bad Debt Expense",
  "Telephone & Communications",
  "Uniforms",
  "Donations & Contributions",
  "Franchise Fees",
  "Drug Testing",
  "Officer Compensation",
  "Retirement Expense",
  "Program Expenses",
  "Housing Allowance",
  "Printing & Reproduction",
  "Teaching Materials",
  "Childcare"
];

export default function TransactionDetailModal({
  transaction,
  reportId,
  onClose,
  onUpdate
}: TransactionDetailModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  useFocusTrap(modalRef, true);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const [showReclassifyForm, setShowReclassifyForm] = useState(false);
  const [newCategory, setNewCategory] = useState(transaction.category);
  const [newSubcategory, setNewSubcategory] = useState(transaction.subcategory || '');
  const [reclassifyReason, setReclassifyReason] = useState('');
  const [history, setHistory] = useState<ReclassificationHistory[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Local state for checked/flagged
  const [isChecked, setIsChecked] = useState(transaction.checked);
  const [isFlagged, setIsFlagged] = useState(transaction.flagged);

  // PDF Viewer state
  const [showPdfViewer, setShowPdfViewer] = useState(false);

  useEffect(() => {
    if (transaction.is_reclassified) {
      fetchReclassificationHistory();
    }
  }, [transaction.id]);

  const fetchReclassificationHistory = async () => {
    try {
      const data = await api.get(`/api/v1/reconciliation/history/${reportId}?transaction_id=${transaction.id}`);
      setHistory(data.history || []);
    } catch (err) {
      console.error('Failed to fetch history:', err);
    }
  };

  const handleReclassify = async () => {
    if (!reclassifyReason.trim() || reclassifyReason.trim().length < 10) {
      setError('Please provide a reason for reclassification (minimum 10 characters)');
      return;
    }

    if (newCategory === transaction.category && newSubcategory === transaction.subcategory) {
      setError('Please select a different category');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await api.post(`/api/v1/reconciliation/transactions/${reportId}/${transaction.id}/reclassify`, {
        new_category: newCategory,
        new_subcategory: newSubcategory || null,
        reason: reclassifyReason
      });

      setShowReclassifyForm(false);
      setReclassifyReason('');
      onUpdate();
    } catch (err: any) {
      console.error('Reclassification failed:', err);
      setError(err.response?.data?.detail || 'Failed to reclassify transaction');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleChecked = async () => {
    try {
      await api.patch(`/api/v1/reconciliation/transactions/${reportId}/${transaction.id}`, {
        checked: !isChecked
      });
      setIsChecked(!isChecked);
      onUpdate();
    } catch (err) {
      console.error('Failed to toggle checked:', err);
    }
  };

  const handleToggleFlagged = async () => {
    try {
      await api.patch(`/api/v1/reconciliation/transactions/${reportId}/${transaction.id}`, {
        flagged: !isFlagged
      });
      setIsFlagged(!isFlagged);
      onUpdate();
    } catch (err) {
      console.error('Failed to toggle flagged:', err);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div ref={modalRef} role="dialog" aria-modal="true" aria-label="Transaction Detail" className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
          <h2 className="text-xl font-bold text-gray-900">Transaction Detail</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Date</label>
              <p className="text-sm text-gray-900 mt-1">
                {new Date(transaction.date).toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric'
                })}
              </p>
            </div>
            <div>
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Amount</label>
              <p className="text-lg font-bold text-gray-900 mt-1">
                {new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: 'USD'
                }).format(transaction.amount)}
              </p>
            </div>
          </div>

          {/* Vendor & Description */}
          <div>
            <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Vendor</label>
            <p className="text-sm text-gray-900 mt-1 font-medium">{transaction.vendor || transaction.description || '—'}</p>
          </div>

          <div>
            <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Description</label>
            <p className="text-sm text-gray-700 mt-1">{transaction.description || transaction.vendor || '—'}</p>
          </div>

          {/* Source Document */}
          <div className="bg-blue-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <label className="text-xs font-bold text-blue-900 uppercase tracking-wide">Source Document</label>
                <p className="text-sm text-blue-800 mt-1">{transaction.source_file}</p>
                <p className="text-xs text-blue-600 mt-0.5">Page {transaction.source_page}</p>
              </div>
              <button
                onClick={() => setShowPdfViewer(true)}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700"
              >
                <FileText size={16} />
                View PDF
              </button>
            </div>
          </div>

          {/* Current Classification */}
          <div>
            <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Current Classification</label>
            <div className="mt-2 space-y-2">
              <div className="flex items-center gap-2">
                <span className={`inline-block px-3 py-1.5 text-sm rounded-full font-medium ${
                  transaction.is_reclassified
                    ? 'bg-blue-100 text-blue-800 border border-blue-300'
                    : 'bg-green-100 text-green-800'
                }`}>
                  {transaction.category}
                  {transaction.subcategory && ` → ${transaction.subcategory}`}
                </span>
                {transaction.is_reclassified && (
                  <span className="text-xs text-blue-600 font-medium">RECLASSIFIED</span>
                )}
              </div>

              {transaction.is_reclassified && transaction.original_category && (
                <div className="text-xs text-gray-600 bg-gray-50 rounded px-3 py-2">
                  <strong>Originally:</strong> {transaction.original_category}
                  {transaction.reclassification_reason && (
                    <div className="mt-1 italic">
                      <strong>Reason:</strong> {transaction.reclassification_reason}
                    </div>
                  )}
                </div>
              )}

              {/* Show history button if reclassified */}
              {transaction.is_reclassified && (
                <button
                  onClick={() => setShowHistory(!showHistory)}
                  className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 font-medium"
                >
                  <History size={16} />
                  {showHistory ? 'Hide' : 'View'} Reclassification History
                </button>
              )}

              {/* History Display */}
              {showHistory && history.length > 0 && (
                <div className="border border-gray-200 rounded-lg p-4 space-y-2 max-h-40 overflow-y-auto">
                  {history.map((entry, idx) => (
                    <div key={idx} className="text-xs border-b border-gray-100 pb-2 last:border-0">
                      <div className="flex justify-between items-start mb-1">
                        <span className="font-medium text-gray-900">{entry.user || 'Unknown'}</span>
                        <span className="text-gray-500">
                          {entry.timestamp ? new Date(entry.timestamp).toLocaleString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit'
                          }) : '—'}
                        </span>
                      </div>
                      <div className="text-gray-700">
                        <span className="line-through">{entry.from_category || '—'}</span> → <span className="font-medium">{entry.to_category || '—'}</span>
                      </div>
                      {entry.reason && <div className="text-gray-600 italic mt-1">{entry.reason}</div>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Flags */}
          {transaction.flagged && transaction.flag_reason && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="text-yellow-600 flex-shrink-0" size={20} />
              <div>
                <label className="text-xs font-bold text-yellow-900 uppercase tracking-wide">Flag Reason</label>
                <p className="text-sm text-yellow-800 mt-1">{transaction.flag_reason}</p>
              </div>
            </div>
          )}

          {transaction.needs_review && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
              <AlertCircle className="text-red-600" size={16} />
              <span className="text-sm text-red-800 font-medium">This transaction needs review</span>
            </div>
          )}

          {/* Review Status Checkboxes */}
          <div className="flex items-center gap-6 pt-4 border-t">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={isChecked}
                onChange={handleToggleChecked}
                className="rounded border-gray-300 text-green-600 focus:ring-green-500"
              />
              <CheckCircle className={`${isChecked ? 'text-green-600' : 'text-gray-400'}`} size={16} />
              <span className={isChecked ? 'text-green-700 font-medium' : 'text-gray-700'}>Reviewed</span>
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={isFlagged}
                onChange={handleToggleFlagged}
                className="rounded border-gray-300 text-yellow-600 focus:ring-yellow-500"
              />
              <AlertCircle className={`${isFlagged ? 'text-yellow-600' : 'text-gray-400'}`} size={16} />
              <span className={isFlagged ? 'text-yellow-700 font-medium' : 'text-gray-700'}>Flagged</span>
            </label>
          </div>

          {/* Reclassification Form */}
          {!showReclassifyForm ? (
            <button
              onClick={() => setShowReclassifyForm(true)}
              className="w-full px-4 py-3 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
            >
              🔄 Reclassify Transaction
            </button>
          ) : (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-5 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide">Reclassification Form</h3>
                <button
                  onClick={() => {
                    setShowReclassifyForm(false);
                    setError('');
                    setNewCategory(transaction.category);
                    setNewSubcategory(transaction.subcategory || '');
                    setReclassifyReason('');
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={18} />
                </button>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-700">
                  {error}
                </div>
              )}

              {/* Category Dropdown */}
              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wide mb-2">
                  New Category <span className="text-red-600">*</span>
                </label>
                <select
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {BI_CATEGORIES.map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              {/* Subcategory (optional) */}
              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wide mb-2">
                  Subcategory (Optional)
                </label>
                <input
                  type="text"
                  value={newSubcategory}
                  onChange={(e) => setNewSubcategory(e.target.value)}
                  placeholder="e.g., Fixed, Variable, Specific detail..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Reason (required) */}
              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wide mb-2">
                  Reason for Reclassification <span className="text-red-600">*</span>
                </label>
                <textarea
                  value={reclassifyReason}
                  onChange={(e) => setReclassifyReason(e.target.value)}
                  placeholder="Explain why this transaction is being reclassified (minimum 10 characters)..."
                  className="w-full h-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {reclassifyReason.length}/10 characters minimum
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowReclassifyForm(false);
                    setError('');
                    setNewCategory(transaction.category);
                    setNewSubcategory(transaction.subcategory || '');
                    setReclassifyReason('');
                  }}
                  disabled={loading}
                  className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleReclassify}
                  disabled={loading || reclassifyReason.length < 10}
                  className="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Reclassifying...' : '✓ Confirm Reclassification'}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 border-t border-gray-200 sticky bottom-0 bg-white">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Close
          </button>
        </div>
      </div>

      {/* PDF Viewer Modal */}
      <PDFViewerModal
        isOpen={showPdfViewer}
        reportId={reportId}
        sourceFile={transaction.source_file}
        sourcePage={transaction.source_page}
        transactionContext={{
          date: transaction.date,
          vendor: transaction.vendor,
          amount: transaction.amount
        }}
        onClose={() => setShowPdfViewer(false)}
      />
    </div>
  );
}
